import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComponentState,
  TimeCheckboxField,
  TimeFieldValidation,
  TimeFieldValidationErrors,
  TimeInfoValidationResult,
  FeiertageTimeData
} from '../types';

const TIME_CHECKBOX_FIELDS: TimeCheckboxField[] = ['jahr', 'monat', 'woche', 'tag', 'feiertag'];

/**
 * Validates a single time field's data
 */
function validateTimeField(
  fieldId: TimeCheckboxField,
  data: ComponentState[TimeCheckboxField]['data'],
  checked: boolean,
  t: (key: string) => string
): TimeFieldValidation {
  const errors: TimeFieldValidationErrors = {};

  // Skip validation if checkbox is not checked
  if (!checked) {
    return { isValid: true, errors: {} };
  }

  // 1. Datenform is required when checkbox is checked
  if (!data.datenform || data.datenform === '') {
    errors.datenform = t('timeInformation.validation.datenformRequired');
  }

  // 2. Zeithorizont validation when datenform is "Zeithorizont"
  if (data.datenform === 'Zeithorizont') {
    // Check if zeithorizontStart is provided and valid
    if (!data.zeithorizontStart || data.zeithorizontStart.trim() === '') {
      errors.zeithorizontStart = t('timeInformation.validation.zeithorizontStartRequired');
    } else {
      const startValue = parseFloat(data.zeithorizontStart);
      if (isNaN(startValue)) {
        errors.zeithorizontStart = t('timeInformation.validation.zeithorizontInvalidNumber');
      }
    }

    // Check if zeithorizontEnd is provided and valid
    if (!data.zeithorizontEnd || data.zeithorizontEnd.trim() === '') {
      errors.zeithorizontEnd = t('timeInformation.validation.zeithorizontEndRequired');
    } else {
      const endValue = parseFloat(data.zeithorizontEnd);
      if (isNaN(endValue)) {
        errors.zeithorizontEnd = t('timeInformation.validation.zeithorizontInvalidNumber');
      }
    }

    // Check if start <= end (only if both are valid numbers)
    if (data.zeithorizontStart && data.zeithorizontEnd) {
      const startValue = parseFloat(data.zeithorizontStart);
      const endValue = parseFloat(data.zeithorizontEnd);
      if (!isNaN(startValue) && !isNaN(endValue) && startValue > endValue) {
        errors.zeithorizontStart = t('timeInformation.validation.zeithorizontStartGreaterThanEnd');
      }
    }
  }

  // 3. Skalierung validation when skalierung is "ja"
  if (data.skalierung === 'ja') {
    // Check if skalierungMin is provided and valid
    if (!data.skalierungMin || data.skalierungMin.trim() === '') {
      errors.skalierungMin = t('timeInformation.validation.skalierungMinRequired');
    } else {
      const minValue = parseFloat(data.skalierungMin);
      if (isNaN(minValue)) {
        errors.skalierungMin = t('timeInformation.validation.skalierungInvalidNumber');
      }
    }

    // Check if skalierungMax is provided and valid
    if (!data.skalierungMax || data.skalierungMax.trim() === '') {
      errors.skalierungMax = t('timeInformation.validation.skalierungMaxRequired');
    } else {
      const maxValue = parseFloat(data.skalierungMax);
      if (isNaN(maxValue)) {
        errors.skalierungMax = t('timeInformation.validation.skalierungInvalidNumber');
      }
    }

    // Check if min <= max (only if both are valid numbers)
    if (data.skalierungMin && data.skalierungMax) {
      const minValue = parseFloat(data.skalierungMin);
      const maxValue = parseFloat(data.skalierungMax);
      if (!isNaN(minValue) && !isNaN(maxValue) && minValue > maxValue) {
        errors.skalierungMin = t('timeInformation.validation.skalierungMinGreaterThanMax');
      }
    }
  }

  // 4. Land validation for feiertag field
  if (fieldId === 'feiertag') {
    const feiertageData = data as FeiertageTimeData;
    if (!feiertageData.land || feiertageData.land === '') {
      errors.land = t('timeInformation.validation.landRequired');
    }
  }

  const isValid = Object.keys(errors).length === 0;
  return { isValid, errors };
}

/**
 * Hook for validating TimeInformation component state
 * Returns validation result with field-level errors
 */
export function useTimeInfoValidation(state: ComponentState): TimeInfoValidationResult {
  const { t } = useTranslation('training');

  return useMemo(() => {
    const fieldValidations = {} as Record<TimeCheckboxField, TimeFieldValidation>;
    let isValid = true;

    for (const fieldId of TIME_CHECKBOX_FIELDS) {
      const fieldState = state[fieldId];
      const validation = validateTimeField(fieldId, fieldState.data, fieldState.checked, t);
      fieldValidations[fieldId] = validation;

      if (!validation.isValid) {
        isValid = false;
      }
    }

    return {
      isValid,
      fieldValidations
    };
  }, [state, t]);
}

export default useTimeInfoValidation;
